import {localPoint} from '@visx/event';
import {Group} from '@visx/group';
import {ScaleSVG} from '@visx/responsive';
import {scaleBand, scaleLinear} from '@visx/scale';
import {Bar} from '@visx/shape';
import {useTooltip, useTooltipInPortal} from '@visx/tooltip';
import {useMemo} from 'react';

import {ChartTooltip} from '../ChartTooltip';
import {
  AXIS_BOTTOM_OFFSET,
  AXIS_LEFT_OFFSET,
  AXIS_TOP_OFFSET,
  TOOLTIP_DEBOUNCE,
} from '../utils/constants';

import {ChartAxis} from './ChartAxis';

import type {PieBarDatum} from '../utils/types';

// Define a fixed viewBox size for consistent scaling
const viewBoxWidth = 800;
const viewBoxHeight = 400;

type Props<T extends PieBarDatum> = {
  data: T[];
  handleDatumClick?: (item: T) => void;
  renderTooltipLabel?: (item: T) => {
    title: React.ReactNode;
    value: React.ReactNode;
  };
};
export function BarChart<T extends PieBarDatum>({
  data,
  handleDatumClick,
  renderTooltipLabel,
}: Props<T>): React.JSX.Element {
  // const xMax = width - margin.left - margin.right;
  // const yMax = height - margin.top - margin.bottom - AXIS_TOP_OFFSET;
  const xMax = viewBoxWidth - AXIS_LEFT_OFFSET;
  const yMax = viewBoxHeight - AXIS_TOP_OFFSET - AXIS_BOTTOM_OFFSET;

  const xScale = useMemo(
    () =>
      scaleBand<string>({
        domain: data.map((data) => data.name),
        range: [0, xMax],
        round: true,
        padding: 0.2,
      }),
    [data, xMax]
  );
  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [0, Math.max(...data.map(({value}) => value))],
        range: [yMax, 0],
        round: true,
        nice: true,
      }),
    [data, yMax]
  );

  const tt = useTooltip<T>();
  const {containerRef, TooltipInPortal} = useTooltipInPortal({
    scroll: true,
    debounce: TOOLTIP_DEBOUNCE,
  });

  return (
    <div className="relative h-full w-full" ref={containerRef}>
      <ScaleSVG width={viewBoxWidth} height={viewBoxHeight}>
        <Group top={AXIS_TOP_OFFSET} left={AXIS_LEFT_OFFSET}>
          {data.map((datum) => (
            <Bar
              key={datum.name}
              x={xScale(datum.name)}
              y={yScale(datum.value)}
              width={xScale.bandwidth()}
              fill={datum.fill}
              height={
                viewBoxHeight -
                AXIS_TOP_OFFSET -
                AXIS_BOTTOM_OFFSET -
                yScale(datum.value)
              }
              onClick={() => handleDatumClick?.(datum)}
              onMouseLeave={tt.hideTooltip}
              onMouseMove={(event) => {
                const _event = localPoint(event);
                tt.showTooltip({
                  tooltipData: datum,
                  tooltipTop: _event?.y,
                  tooltipLeft: _event?.x,
                });
              }}
            />
          ))}
        </Group>

        <ChartAxis height={viewBoxHeight} xScale={xScale} yScale={yScale} />
      </ScaleSVG>

      {tt.tooltipOpen && tt.tooltipData && renderTooltipLabel && (
        <ChartTooltip
          Component={TooltipInPortal}
          helpers={tt}
          renderTooltipLabel={renderTooltipLabel}
        />
      )}
    </div>
  );
}

BarChart.displayName = 'BarChart';
